import React from 'react';
import type { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form';
import type { AgeCategory, CalculatorMode } from '../../types/tax';
import AdvancedOptionsSection from './AdvancedOptionsSection';
import PresetButtons from './PresetButtons';

export interface FormData {
  income: string;
  ageCategory: AgeCategory | '';
  incomeFrequency: 'monthly' | 'yearly';
  // Advanced options
  showAdvancedOptions: boolean;
  // Medical Aid
  hasMedicalAid: boolean;
  medicalAidMainMember: boolean;
  medicalAidDependents: string;
  // Pension
  hasPension: boolean;
  pensionType: 'percentage' | 'fixed';
  pensionAmount: string;
  // Travel Allowance
  hasTravelAllowance: boolean;
  vehicleValue: string;
  privateUsePercentage: string;
  travelAllowanceAmount: string;
}

interface CalculationFormDataProps {
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  isCalculating?: boolean;
  watchedValues: FormData;
  setValue: UseFormSetValue<FormData>;
  showPresets?: boolean;
  showFrequencyToggle?: boolean;
  mode?: CalculatorMode;
}

export default function CalculationFormData({ 
  register, 
  errors, 
  isCalculating = false, 
  watchedValues,
  setValue,
  showPresets = true,
  showFrequencyToggle = true,
  mode = 'reverse'
}: CalculationFormDataProps) {
  
  const isReverseMode = mode === 'reverse';
  
  const formatCurrencyInput = (value: string): string => {
    const numericValue = value.replace(/[^\d]/g, '');
    if (!numericValue) return '';
    
    const formatted = parseInt(numericValue).toLocaleString('en-ZA');
    return `R ${formatted}`;
  };

  const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatCurrencyInput(rawValue);
    e.target.value = formattedValue;
  };

  const handlePresetSelect = (monthlyAmount: number) => {
    // Convert to the appropriate frequency and format
    const amount = watchedValues.incomeFrequency === 'yearly' 
      ? monthlyAmount * 12 
      : monthlyAmount;
    
    const formattedValue = formatCurrencyInput(amount.toString());
    setValue('income', formattedValue);
  };
  
  const incomeLabel = isReverseMode 
    ? 'Desired Net (Take-home) Income' 
    : 'Gross Income';
  
  const incomeDescription = isReverseMode
    ? `Enter your desired ${watchedValues.incomeFrequency === 'yearly' ? 'annual' : 'monthly'} take-home pay after all deductions`
    : `Enter your ${watchedValues.incomeFrequency === 'yearly' ? 'annual' : 'monthly'} gross salary before deductions`;
  
  const incomePlaceholder = watchedValues.incomeFrequency === 'yearly' ? 'R 500,000' : 'R 50,000';

  return (
    <div className="space-y-6">
      {/* Income Frequency Toggle */}
      {showFrequencyToggle && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
            Income Period
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="monthly"
                {...register('incomeFrequency', { required: 'Please select income period' })}
                className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                disabled={isCalculating}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Monthly</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="yearly"
                {...register('incomeFrequency', { required: 'Please select income period' })}
                className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                disabled={isCalculating}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Yearly</span>
            </label>
          </div>
          {errors.incomeFrequency && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.incomeFrequency.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-500">
            Most South Africans work with monthly salaries
          </p>
        </div>
      )}

      <div>
        <label htmlFor="income" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          {incomeLabel}
          <span className="text-gray-500 dark:text-slate-500 text-xs ml-1">
            (per {watchedValues.incomeFrequency || 'month'})
          </span>
        </label>
        <div className="relative">
          <input
            type="text"
            id="income"
            {...register('income', {
              required: isReverseMode 
                ? 'Please enter your desired net income' 
                : 'Please enter your gross income',
              validate: (value) => {
                const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
                if (isNaN(numericValue) || numericValue <= 0) {
                  return 'Please enter a valid amount';
                }
                
                const isMonthly = watchedValues.incomeFrequency === 'monthly';
                const minAmount = isMonthly ? 1000 : 12000;
                const maxAmount = isMonthly ? 4000000 : 50000000;
                
                if (numericValue < minAmount) {
                  return `Please enter an amount of at least R ${minAmount.toLocaleString()}`;
                }
                if (numericValue > maxAmount) {
                  return `Please enter an amount less than R ${maxAmount.toLocaleString()}`;
                }
                return true;
              }
            })}
            onChange={handleCurrencyInput}
            placeholder={incomePlaceholder}
            className={`
              input-field w-full pl-3 pr-12
              ${errors.income ? 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
            `}
            disabled={isCalculating}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500 dark:text-slate-500 text-sm">ZAR</span>
          </div>
        </div>
        {errors.income && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.income.message}</p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-slate-500">
          {incomeDescription}
        </p>
        
        {/* Preset Buttons */}
        {showPresets && (
          <div className="mt-4">
            <PresetButtons
              onPresetSelect={handlePresetSelect}
              disabled={isCalculating}
              currentFrequency={watchedValues.incomeFrequency || 'monthly'}
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Age Category
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              value="under65"
              {...register('ageCategory', { required: 'Please select an age category' })}
              className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
              disabled={isCalculating}
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Under 65 years</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="age65to74"
              {...register('ageCategory', { required: 'Please select an age category' })}
              className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
              disabled={isCalculating}
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">65 to 74 years</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="age75plus"
              {...register('ageCategory', { required: 'Please select an age category' })}
              className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
              disabled={isCalculating}
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">75 years and older</span>
          </label>
        </div>
        {errors.ageCategory && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.ageCategory.message}</p>
        )}
        <p className="mt-1 text-xs text-gray-500 dark:text-slate-500">
          Age affects tax rebates and thresholds
        </p>
      </div>

      {/* Advanced Options Toggle */}
      <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="showAdvancedOptions"
            {...register('showAdvancedOptions')}
            className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green"
            disabled={isCalculating}
          />
          <label htmlFor="showAdvancedOptions" className="ml-2 text-sm font-medium text-gray-700 dark:text-slate-300">
            Include advanced tax options
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-slate-500 ml-6">
          Medical aid, pension contributions, and company vehicle benefits
        </p>
      </div>

      {/* Advanced Options Section */}
      <AdvancedOptionsSection
        register={register}
        errors={errors}
        watchedValues={watchedValues}
        isCalculating={isCalculating}
        setValue={setValue}
      />
    </div>
  );
}